import createRaf from "@solid-primitives/raf";
import { createEffect, createMemo, createSignal, For, type JSX, onCleanup, onMount, type Signal } from "solid-js";
import { Portal, render } from "solid-js/web";
import { game } from "~/js/game";
import { Pages } from "~/js/pages";
import { getCurrency } from "~/js/prefs";
import select from "~/js/select";
import { SoundManager } from "~/js/sounds";
import { addStat } from "~/js/stats";
import { animateWithClass, createPage } from "~/js/utils";
import { getPlayerLevel, numToValuta } from "~/script";
import { user } from "~/userStore";

const clickEffects = {
	"2x": {
		icon: "/images/2x-ce.png",
		name: "2X",
		description: "2X Money",
	},
	"4x": {
		icon: "/images/4x-ce.png",
		name: "4X",
		description: "4X Money",
	},
	"7x": {
		icon: "/images/7x-ce.png",
		name: "7X",
		description: "7X Money",
	},
	cashbag: {
		icon: "/images/cb-ce.png",
		name: "Cash Bag",
		description: "Cashbag",
	},
} as const;
export type ClickEffectType = keyof typeof clickEffects;

function ClickPage() {
	const clickHistoryWindow = 2; // seconds

	const [running, start, stop] = createRaf(updateClickEffects);

	const [clickHistory, setClickHistory] = createSignal<number[]>([]);

	const [activeClickEffects, setActiveClickEffects] = createSignal<{ multiplier: number; duration: Signal<number>; endTime: number }[]>([]);

	const [clickEffectActivators, setClickEffectsActivators] = createSignal<{ type: ClickEffectType; pos: { x: number; y: number } }[]>([]);

	const [floatingTexts, setFloatingTexts] = createSignal<{ x: number; y: number; valueType: "xp" | "money"; value: number }[]>([]);

	const cps = createMemo(() => clickHistory().length / 2);
	const moneyMultiplier = createMemo(() => {
		return activeClickEffects().reduce((acc, effect) => acc * effect.multiplier, 1);
	});

	let lastClick = 0;
	function canSummonNewCe() {
		// enforce that random click effects can only happen once every 100ms
		if (performance.now() > lastClick) {
			lastClick = performance.now() + 100;
			return true;
		}
		return false;
	}

	function updateClickEffects(now: number) {
		setActiveClickEffects((prev) =>
			prev.reduce<{ multiplier: number; duration: Signal<number>; endTime: number }[]>((acc, effect) => {
				if (effect.endTime > now) {
					effect.duration[1]((effect.endTime - now) / 1000);
					acc.push(effect);
				}
				return acc;
			}, []),
		);
	}
	createEffect(() => {
		if (!running() && activeClickEffects().length > 0) {
			start();
		} else if (running() && activeClickEffects().length === 0) {
			stop();
		}
	});

	const clearClicksInterval = setInterval(() => {
		setClickHistory((prev) => prev.filter((ts) => ts >= performance.now() - clickHistoryWindow * 1000));
	}, 500);
	onCleanup(() => {
		clearInterval(clearClicksInterval);
	});

	const handleClick: JSX.EventHandler<HTMLDivElement, MouseEvent | TouchEvent> = (event) => {
		if (event.target.classList.contains("ce-activator")) return; // don't trigger click if clicking on a click effect
		if (!event) return;

		SoundManager.playSound("click");
		const removalThreshold = performance.now() - clickHistoryWindow * 1000;

		setClickHistory((prev) => [...prev.filter((ts) => ts >= removalThreshold), performance.now()]);

		if (
			user.upgrades.unlockClickEffect2x &&
			Math.random() < (1 / 250) * user.upgrades.clickEffectsChance &&
			canSummonNewCe() &&
			cps() < 24
		) {
			const unlockedUpgrades: ClickEffectType[] = ["2x"];
			if (user.upgrades.unlockClickEffect4x) unlockedUpgrades.push("4x");
			if (user.upgrades.unlockClickEffect7x) unlockedUpgrades.push("7x");
			if (user.upgrades.effectCashBag) unlockedUpgrades.push("cashbag");
			const randomEffect = unlockedUpgrades[Math.floor(Math.random() * unlockedUpgrades.length)];
			if (!randomEffect) throw new Error("No click effect found");
			setClickEffectsActivators((effects) => [...effects, { type: randomEffect, pos: { x: Math.random(), y: Math.random() } }]);
		}

		let moneyPerClick = user.upgrades.perClick * user.upgrades.perClickMul * (1 + getPlayerLevel().level * 0.05);
		moneyPerClick += user.upgrades.perClick * user.upgrades.perClickMul * (Math.random() * 0.2 - 0.1); // random variation of +/- 10%
		moneyPerClick *= moneyMultiplier();

		let xpPerClick =
			Math.round(Math.random() * 6 + 1) * moneyMultiplier() * user.upgrades.perClickXp * user.upgrades.xpmul * user.upgrades.xpMulMul;

		if (cps() >= 24) {
			moneyPerClick = 0;
			xpPerClick = 0;
		}
		addStat("earned_cash", moneyPerClick);
		addStat("earnings_from_clicks", moneyPerClick);
		addStat("earned_xp", xpPerClick);
		addStat("xp_from_clicks", xpPerClick);
		const pageX =
			"pageX" in event
				? event.pageX
				: "changedTouches" in event && event.changedTouches.length > 0
					? event.changedTouches[0]!.pageX
					: undefined;
		const pageY =
			"pageY" in event
				? event.pageY
				: "changedTouches" in event && event.changedTouches.length > 0
					? event.changedTouches[0]!.pageY
					: undefined;

		setFloatingTexts((prev) => [
			...prev,
			{ x: pageX ?? 0, y: pageY ?? 0, valueType: "xp", value: xpPerClick },
			{ x: pageX ?? 0, y: pageY ?? 0, valueType: "money", value: moneyPerClick },
		]);
		addStat("clicks");
	};

	return (
		<div class="center-v" on:mousedown={handleClick} on:touchstart={handleClick}>
			<div class="clickbtn">{getCurrency()}</div>
			<div class="click-how2-text">Click to earn money</div>
			<div class="click-how2-text"> Current money multiplier: {moneyMultiplier()}X</div>
			<div class="click-how2-text">
				<span>
					{cps() > 24
						? "You are clicking too fast"
						: `${numToValuta(user.upgrades.perClick * user.upgrades.perClickMul * (1 + getPlayerLevel().level * 0.05) * moneyMultiplier() * cps(), 2)}/s`}
				</span>
			</div>
			<div class="click-ce-list">
				<For each={activeClickEffects()}>
					{(effect) => (
						<div class="click-ce-list-item">
							{effect.multiplier}X Money - {effect.duration[0]().toFixed(2)} seconds
						</div>
					)}
				</For>
			</div>

			<For each={clickEffectActivators()}>
				{(effect, i) => (
					<ClickEffect
						click={(e) => {
							setClickEffectsActivators((effects) => effects.filter((_, idx) => idx !== i()));
							switch (effect.type) {
								case "2x":
									setActiveClickEffects((prev) => [
										...prev,
										{
											multiplier: 2,
											duration: createSignal(Math.ceil(10 * user.upgrades.clickEffectsDuration)),
											endTime: performance.now() + 10000,
										},
									]);
									break;
								case "4x":
									setActiveClickEffects((prev) => [
										...prev,
										{
											multiplier: 4,
											duration: createSignal(Math.ceil(8 * user.upgrades.clickEffectsDuration)),
											endTime: performance.now() + 8000,
										},
									]);
									break;
								case "7x":
									setActiveClickEffects((prev) => [
										...prev,
										{
											multiplier: 7,
											duration: createSignal(Math.ceil(5 * user.upgrades.clickEffectsDuration)),
											endTime: performance.now() + 5000,
										},
									]);
									break;
								case "cashbag": {
									const t = Math.ceil(user.upgrades.perClick * user.upgrades.perClickMul * 200 * user.upgrades.effectCashBag);
									setFloatingTexts((prev) => [...prev, { x: e.pageX, y: e.pageY, valueType: "money", value: t }]);
									break;
								}
							}
						}}
						decay={() => {
							setClickEffectsActivators((effects) => effects.filter((_, idx) => idx !== i()));
						}}
						effectType={effect.type}
						pos={effect.pos}
					/>
				)}
			</For>

			<For each={floatingTexts()}>
				{(text) => (
					<FloatingText
						onDone={() => setFloatingTexts((prev) => prev.filter((t) => t !== text))}
						value={text.value}
						valueType={text.valueType}
						x={text.x}
						y={text.y}
					/>
				)}
			</For>
		</div>
	);
}

function ClickEffect(props: {
	effectType: ClickEffectType;
	pos: { x: number; y: number };
	decay: () => void;
	click: JSX.EventHandler<HTMLDivElement, MouseEvent>;
}) {
	const clickEffect = clickEffects[props.effectType];

	addStat("click_effects_summoned");
	addStat(`click_effects_summoned_${props.effectType}`);

	const effectDecayTime = Math.ceil(8 * user.upgrades.clickEffectsDecay);
	const [activated, setActivated] = createSignal(false);

	SoundManager.playSound("power-appear");

	return (
		<div
			on:animationend={(e) => {
				if (!activated() && e.animationName === "decay") {
					props.decay();
					addStat("click_effects_decayed");
					addStat(`click_effects_decayed_${props.effectType}`);
				}
			}}
			class="ce-activator-container"
			style={{
				left: `calc(60px + ${props.pos.x * 100} * (100% - 120px) / 100)`,
				top: `calc(60px + ${props.pos.y * 100} * (100% - 120px) / 100)`,
				animation: `decay ${effectDecayTime}s linear forwards`,
				transform: "translate(-50%, -50%)",
				"pointer-events": "auto",
			}}
		>
			<div
				on:animationend={(e) => {
					if (e.animationName === "explode") {
						props.decay();
					}
				}}
				class="ce-activator"
				classList={{ "ce-activated": activated() }}
				onclick={(e) => {
					if (activated()) return;
					SoundManager.playSound("power-activate");
					setActivated(true);
					props.click(e);
					addStat("click_effects_activated");
					addStat(`click_effects_activated_${props.effectType}`);
				}}
				style={{ "background-image": `url(${clickEffect.icon})` }}
			/>
		</div>
	);
}

function FloatingText(props: { x: number; y: number; valueType: "xp" | "money"; value: number; onDone?: () => void }) {
	const [left, setLeft] = createSignal(props.x);
	const [top, setTop] = createSignal(props.y);
	const [opacity, setOpacity] = createSignal(1);
	let el: HTMLDivElement | undefined;

	onMount(() => {
		if (!el) return;

		const w = el.offsetWidth;
		const h = el.offsetHeight;

		const startX = props.x - w / 2;
		const startY = props.y - h;

		const t1 = setTimeout(() => {
			setLeft(startX + Math.random() * 60 - 30);
			setTop(startY - Math.random() * 100 - 40);
		}, 50);

		const t2 = setTimeout(() => {
			setOpacity(0);
			const onEnd = () => props.onDone?.();
			el?.addEventListener("transitionend", onEnd, { once: true });
		}, 400);

		if (props.valueType === "money") {
			const moneyElement = select("#money");
			if (!moneyElement) throw new Error("No money element found");
			animateWithClass(moneyElement, "scale2x", undefined, () => {
				user.money += props.value;
				game.emit("update");
			});
		} else {
			const xpElement = select("#level-bar-p");
			if (!xpElement) throw new Error("No xp element found");
			animateWithClass(xpElement, "level-bar-highlight", undefined, () => {
				user.xp += props.value;
				game.emit("update"); //TODO: move this off of the animate with class and just do on animation end manually
			});
		}

		onCleanup(() => {
			clearTimeout(t1);
			clearTimeout(t2);
		});
	});

	return (
		<Portal>
			<div
				class="floating-text"
				ref={(ref) => {
					el = ref;
				}}
				style={{
					left: `${left()}px`,
					top: `${top()}px`,
					opacity: opacity(),
				}}
			>
				{`+ ${props.valueType === "money" ? numToValuta(props.value, 2) : `${props.value.toFixed(0)} xp`}`}
			</div>
		</Portal>
	);
}

createPage(() => <ClickPage />, "click");
